import { Y_TICK_COUNT } from './cartesianChart'
import { MetricUnit } from './types'

/** Decimal ceiling for a K/M tick label. A step of 1 — the smallest the
 *  1 / 2 / 2.5 / 5 ladder yields on a count axis — needs exactly three places at
 *  the K unit, and the M unit is only taken when the step fits there too, so this
 *  bounds the search without ever truncating a real step. */
const MAX_TICK_DECIMALS = 3

/** True when `scaled` survives `decimals` places unchanged. */
const fitsIn = (scaled: number, decimals: number): boolean =>
  Number(scaled.toFixed(decimals)) === scaled

/** Decimal places that render `scaled` — a tick step divided by its K/M unit —
 *  without rounding, capped at `maxDecimals`. Every tick is a whole multiple of
 *  the step, so a precision that holds the step holds all of them. */
function decimalsForStep(scaled: number, maxDecimals: number): number {
  for (let decimals = 0; decimals < maxDecimals; decimals += 1) {
    if (fitsIn(scaled, decimals)) return decimals
  }
  return maxDecimals
}

export function makeYAxisTickFormatter(
  domain: [number, number | string],
  unit?: MetricUnit,
  // Ticks now float with chart height (see fitToGrid); pass the real count so the
  // adjacent-tick step — and the decimal precision derived from it — stays accurate.
  tickCount: number = Y_TICK_COUNT,
): (value: number) => string {
  const [min, max] = domain
  // Recharts uses 'auto' when it controls the domain — fall back to the simple formatter.
  if (typeof max === 'string' || typeof min === 'string')
    return (v) => formatYAxisTick(v, unit)

  const gaps = Math.max(1, tickCount - 1)

  // Percentage axes are typically tight (single digits), so a fixed 1-decimal
  // format reads cleaner than the count-oriented step precision below, and
  // matches the headline value the user sees in the summary panel.
  if (unit === MetricUnit.PERCENTAGE) {
    const range = max - min
    const step = range / gaps
    const decimals = step >= 1 ? 1 : 2
    return (v) => `${v.toFixed(decimals)}%`
  }

  // The domain arrives snapped to [firstTick, lastTick] (@see ./yAxisTicks), so
  // this is the exact gap between adjacent ticks.
  const range = max - min
  const step = range / gaps

  // Sub-1K values: show as plain numbers (e.g. 500, 999).
  if (max < 1_000) return (v) => String(v)

  // Millions only when the step survives three decimals there. A large channel
  // that moves a few hundred followers has a step too fine for the M unit, and
  // every gridline would round to the same label.
  const millions =
    max >= 1_000_000 && fitsIn(step / 1_000_000, MAX_TICK_DECIMALS)
  const scale = millions ? 1_000_000 : 1_000
  const suffix = millions ? 'M' : 'K'
  const decimals = decimalsForStep(step / scale, MAX_TICK_DECIMALS)
  // Number() drops the zeros toFixed pads on, so 5 000 reads "5K" beside 2 500's "2.5K".
  return (v) => `${Number((v / scale).toFixed(decimals))}${suffix}`
}

export function formatYAxisTick(value: number, unit?: MetricUnit): string {
  if (unit === MetricUnit.PERCENTAGE) return `${value.toFixed(1)}%`
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000)
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return String(value)
}
